import React, { useState, useCallback } from "react";
import { 
  SuccessMessage,
  FailMessage,
  Loading,
  FormatValueToDecimal,
  CompanySelect,
  FormatDateToWord,
  YearSelect,
  MonthSelect,
} from "../../common/APICommon";
import { apiUrl } from "../../utils/Configuration";
import axios from 'axios';
import ExcelJS from 'exceljs';
import { PDFDocument } from "pdf-lib";
import fontkit from "@pdf-lib/fontkit";
import NoAccess from "../NoAccess";
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

export default function FinancialStatement() {
//#region Others
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [failMessage, setFailMessage] = useState("");
//#endregion






//#region Variables 
  const [company, setCompany] = useState(null);
  // const today = new Date().toISOString().split("T")[0];
  const [monthFrom, setMonthFrom] = useState(null);
  const [monthTo, setMonthTo] = useState(null);
  const [year, setYear] = useState(null);
//#endregion






//#region Generate
  const fetchReportData = useCallback(async () => {
    const body = {
      monthFrom: monthFrom,
      monthTo: monthTo,
      year: year,
    }

    try {
      // const response = await axios.post(`${apiUrl}/api/Report/report-data/statement-of-account`, body, {
      const response = await axios.post(`${apiUrl}/api/Report/report-data/financial-statement`, body, {
        headers: { 'Content-Type': 'application/json'}
      })
        
      return (response.data)
    } catch (error) {
      console.error('Error fetching data:', error);
      return null
    }
  }, [monthFrom, monthTo, year]);

  // PDF
  const generateBSDetailed = async () => {
    if (!monthFrom || !monthTo || !year || !company) {
      setFailMessage("Please fill up all fields");
      return;
    }

    setLoading(true);
    const allData = await fetchReportData();
    const _data = allData.balanceSheet;

    if (!_data || _data?.length <= 0) {
      setFailMessage("No record found");
      setLoading(false);
      return;
    }

    try {
      const pdfUrl = `${window.location.origin}/docs/BS_DETAILED.pdf`;
      const templateBytes = await fetch(pdfUrl).then((r) => r.arrayBuffer());
      const templateDoc = await PDFDocument.load(templateBytes);
      
      const response = await fetch(pdfUrl);
      const pdfBytes = await response.arrayBuffer();
      const pdfDoc = await PDFDocument.load(pdfBytes);

      pdfDoc.registerFontkit(fontkit);
      const fontResponse = await fetch("/calibri.ttf");
      const fontBytes = await fontResponse.arrayBuffer();
      const calibriFont = await pdfDoc.embedFont(fontBytes);

      const boldResponse = await fetch("/calibrib.ttf");
      const boldBytes = await boldResponse.arrayBuffer();
      const calibriBold = await pdfDoc.embedFont(boldBytes);

      const truncateText = (text, maxWidth, font, fontSize) => {
        if (!text) return "";
        let currentWidth = font.widthOfTextAtSize(text, fontSize);
        if (currentWidth <= maxWidth) return text;
        const ellipsis = "...";
        let truncated = text;
        while (truncated.length > 0 && font.widthOfTextAtSize(truncated + ellipsis, fontSize) > maxWidth) {
          truncated = truncated.slice(0, -1);
        }
        return truncated + ellipsis;
      };

      const drawHeader = (targetPage) => {
        targetPage.drawText(company.name?.toString() ?? "", { x: 35, y: 583, size: 16, font: calibriBold });
        targetPage.drawText(company.address?.toString() ?? "", { x: 35, y: 573, size: 9, font: calibriFont });
        targetPage.drawText(company.tin?.toString() ?? "", { x: 35, y: 563, size: 9, font: calibriFont });

        targetPage.drawText(`${monthFrom} to ${monthTo}`, { x: 83, y: 506, size: 9, font: calibriFont });
        targetPage.drawText(FormatDateToWord(new Date()), { x: 82, y: 495, size: 9, font: calibriFont });
      };

      let pages = pdfDoc.getPages();
      let currentPage = pages[0];
      drawHeader(currentPage);

      let yPosition = 446;
      const bottomThreshold = 80;
      const lineHeight = 12;

      // Helper to handle page breaks
      const addNewPage = async () => {
        const [templatePage] = await pdfDoc.copyPages(templateDoc, [0]);
        currentPage = pdfDoc.addPage(templatePage);
        drawHeader(currentPage);
        yPosition = 446;
      };

      for (const record of _data?.detailed) {
        // --- PAGE BREAK CHECK FOR ITEM ROW ---
        if (yPosition - lineHeight < bottomThreshold) {
          await addNewPage();
        }

        
        currentPage.drawText(`${record.accountType ?? ""}`, { x: 40, y: yPosition, size: 9, font: calibriBold });
        yPosition -= 10;

        for (const itemRow of record?.accounts) {
          // Draw Row Data
          // currentPage.drawText(FormatDateToMDY(order.date), { x: 35, y: yPosition, size: 9, font: calibriFont });
          const truncatedAccount = truncateText(itemRow?.glAccountName ?? "", 120, calibriFont, 8);
          currentPage.drawText(truncatedAccount, { x: 40, y: yPosition, size: 9, font: calibriFont });

          currentPage.drawText(`${FormatValueToDecimal(itemRow.january)}`, { x: 200, y: yPosition, size: 9, font: calibriFont });
          currentPage.drawText(`${FormatValueToDecimal(itemRow.february)}`, { x: 245, y: yPosition, size: 9, font: calibriFont });
          currentPage.drawText(`${FormatValueToDecimal(itemRow.march)}`, { x: 295, y: yPosition, size: 9, font: calibriFont });
          currentPage.drawText(`${FormatValueToDecimal(itemRow.april)}`, { x: 340, y: yPosition, size: 9, font: calibriFont });
          currentPage.drawText(`${FormatValueToDecimal(itemRow.may)}`, { x: 384, y: yPosition, size: 9, font: calibriFont });
          currentPage.drawText(`${FormatValueToDecimal(itemRow.june)}`, { x: 430, y: yPosition, size: 9, font: calibriFont });
          currentPage.drawText(`${FormatValueToDecimal(itemRow.july)}`, { x: 474, y: yPosition, size: 9, font: calibriFont });
          currentPage.drawText(`${FormatValueToDecimal(itemRow.august)}`, { x: 520, y: yPosition, size: 9, font: calibriFont });
          currentPage.drawText(`${FormatValueToDecimal(itemRow.september)}`, { x: 570, y: yPosition, size: 9, font: calibriFont });
          currentPage.drawText(`${FormatValueToDecimal(itemRow.october)}`, { x: 619, y: yPosition, size: 9, font: calibriFont });
          currentPage.drawText(`${FormatValueToDecimal(itemRow.november)}`, { x: 669, y: yPosition, size: 9, font: calibriFont });
          currentPage.drawText(`${FormatValueToDecimal(itemRow.december)}`, { x: 720, y: yPosition, size: 9, font: calibriFont });

          yPosition -= lineHeight;
        }

        currentPage.drawLine({ start: { x: 35, y: yPosition }, end: { x: 757, y: yPosition }, thickness: 1 });
        yPosition -= 15;
        currentPage.drawText(`Total ${record.accountType ?? ""}`, { x: 35, y: yPosition, size: 9, font: calibriBold });
        currentPage.drawText(`${FormatValueToDecimal(record.totals?.january)}`, { x: 200, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.totals?.february)}`, { x: 245, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.totals?.march)}`, { x: 295, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.totals?.april)}`, { x: 340, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.totals?.may)}`, { x: 384, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.totals?.june)}`, { x: 430, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.totals?.july)}`, { x: 474, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.totals?.august)}`, { x: 520, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.totals?.september)}`, { x: 570, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.totals?.october)}`, { x: 619, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.totals?.november)}`, { x: 669, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.totals?.december)}`, { x: 720, y: yPosition, size: 9, font: calibriFont });
        yPosition -= 15;
      }

      // --- FOOTER SECTION (GRAND TOTAL) ---
      if (yPosition < 100) {
        await addNewPage();
      }

      // --- PAGINATION ---
      const allPages = pdfDoc.getPages();
      const totalPages = allPages.length;
      allPages.forEach((page, index) => {
        page.drawText(`Page ${index + 1} of ${totalPages}`, {
          x: 680,
          y: 580,
          size: 10,
          font: calibriBold,
        });
      });

      const modifiedPdfBytes = await pdfDoc.save();
      const blob = new Blob([modifiedPdfBytes], { type: "application/pdf" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `BS_DETAILED.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

    } catch (error) {
      console.error("Error:", error);
      setFailMessage("Error processing PDF");
    }
    setLoading(false);
  };

  const generateBSSummarized = async () => {
    if (!monthFrom || !monthTo || !year || !company) {
      setFailMessage("Please fill up all fields");
      return;
    }

    setLoading(true);
    const allData = await fetchReportData();
    const _data = allData.balanceSheet;

    if (!_data || _data?.length <= 0) {
      setFailMessage("No record found");
      setLoading(false);
      return;
    }

    try {
      const pdfUrl = `${window.location.origin}/docs/BS_SUMMARIZED.pdf`;
      const templateBytes = await fetch(pdfUrl).then((r) => r.arrayBuffer());
      const templateDoc = await PDFDocument.load(templateBytes);
      
      const response = await fetch(pdfUrl);
      const pdfBytes = await response.arrayBuffer();
      const pdfDoc = await PDFDocument.load(pdfBytes);

      pdfDoc.registerFontkit(fontkit);
      const fontResponse = await fetch("/calibri.ttf");
      const fontBytes = await fontResponse.arrayBuffer();
      const calibriFont = await pdfDoc.embedFont(fontBytes);

      const boldResponse = await fetch("/calibrib.ttf");
      const boldBytes = await boldResponse.arrayBuffer();
      const calibriBold = await pdfDoc.embedFont(boldBytes);

      const truncateText = (text, maxWidth, font, fontSize) => {
        if (!text) return "";
        let currentWidth = font.widthOfTextAtSize(text, fontSize);
        if (currentWidth <= maxWidth) return text;
        const ellipsis = "...";
        let truncated = text;
        while (truncated.length > 0 && font.widthOfTextAtSize(truncated + ellipsis, fontSize) > maxWidth) {
          truncated = truncated.slice(0, -1);
        }
        return truncated + ellipsis;
      };

      const drawHeader = (targetPage) => {
        targetPage.drawText(company.name?.toString() ?? "", { x: 35, y: 756, size: 16, font: calibriBold });
        targetPage.drawText(company.address?.toString() ?? "", { x: 35, y: 746, size: 9, font: calibriFont });
        targetPage.drawText(company.tin?.toString() ?? "", { x: 35, y: 736, size: 9, font: calibriFont });

        targetPage.drawText(`${year}`, { x: 90, y: 695, size: 9, font: calibriFont });
        targetPage.drawText(FormatDateToWord(new Date()), { x: 82, y: 685.5, size: 9, font: calibriFont });

        targetPage.drawText(`${year}`, { x: 345, y: 654, size: 9, font: calibriFont });
      };

      let pages = pdfDoc.getPages();
      let currentPage = pages[0];
      drawHeader(currentPage);

      let yPosition = 630;
      const bottomThreshold = 80;
      const lineHeight = 12;

      // Helper to handle page breaks
      const addNewPage = async () => {
        const [templatePage] = await pdfDoc.copyPages(templateDoc, [0]);
        currentPage = pdfDoc.addPage(templatePage);
        drawHeader(currentPage);
        yPosition = 630;
      };

      for (const record of _data?.summary) {
        // --- PAGE BREAK CHECK FOR ITEM ROW ---
        if (yPosition - lineHeight < bottomThreshold) {
          await addNewPage();
        }

        currentPage.drawText(`${record.accountType ?? ""}`, { x: 40, y: yPosition, size: 9, font: calibriBold });
        yPosition -= 10;

        for (const itemRow of record?.accounts) {
          // Draw Row Data
          // currentPage.drawText(FormatDateToMDY(order.date), { x: 35, y: yPosition, size: 9, font: calibriFont });
          const truncatedAccount = truncateText(itemRow?.glAccountName ?? "", 120, calibriFont, 8);
          currentPage.drawText(truncatedAccount, { x: 40, y: yPosition, size: 9, font: calibriFont });

          currentPage.drawText(`${FormatValueToDecimal(itemRow.amountsByYear[0]?.amount)}`, { x: 320, y: yPosition, size: 9, font: calibriFont });

          yPosition -= lineHeight;
        }

        currentPage.drawLine({ start: { x: 35, y: yPosition }, end: { x: 757, y: yPosition }, thickness: 1 });
        yPosition -= 15;
        currentPage.drawText(`Total ${record.accountType ?? ""}`, { x: 35, y: yPosition, size: 9, font: calibriBold });
        currentPage.drawText(`${FormatValueToDecimal(record.totals[0]?.totalAmount)}`, { x: 320, y: yPosition, size: 9, font: calibriFont });
        yPosition -= 15;
      }

      // --- FOOTER SECTION (GRAND TOTAL) ---
      if (yPosition < 100) {
        await addNewPage();
      }

      // --- PAGINATION ---
      const allPages = pdfDoc.getPages();
      const totalPages = allPages.length;
      allPages.forEach((page, index) => {
        page.drawText(`Page ${index + 1} of ${totalPages}`, {
          x: 680,
          y: 580,
          size: 10,
          font: calibriBold,
        });
      });

      const modifiedPdfBytes = await pdfDoc.save();
      const blob = new Blob([modifiedPdfBytes], { type: "application/pdf" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `BS_SUMMARIZED.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

    } catch (error) {
      console.error("Error:", error);
      setFailMessage("Error processing PDF");
    }
    setLoading(false);
  };

  const generateISDetailed = async () => {
    if (!monthFrom || !monthTo || !year || !company) {
      setFailMessage("Please fill up all fields");
      return;
    }

    setLoading(true);
    const allData = await fetchReportData();
    const _data = allData.incomeStatement;

    if (!_data || _data?.length <= 0) {
      setFailMessage("No record found");
      setLoading(false);
      return;
    }

    try {
      const pdfUrl = `${window.location.origin}/docs/IS_DETAILED.pdf`;
      const templateBytes = await fetch(pdfUrl).then((r) => r.arrayBuffer());
      const templateDoc = await PDFDocument.load(templateBytes);
      
      const response = await fetch(pdfUrl);
      const pdfBytes = await response.arrayBuffer();
      const pdfDoc = await PDFDocument.load(pdfBytes);

      pdfDoc.registerFontkit(fontkit);
      const fontResponse = await fetch("/calibri.ttf");
      const fontBytes = await fontResponse.arrayBuffer();
      const calibriFont = await pdfDoc.embedFont(fontBytes);

      const boldResponse = await fetch("/calibrib.ttf");
      const boldBytes = await boldResponse.arrayBuffer();
      const calibriBold = await pdfDoc.embedFont(boldBytes);

      const truncateText = (text, maxWidth, font, fontSize) => {
        if (!text) return "";
        let currentWidth = font.widthOfTextAtSize(text, fontSize);
        if (currentWidth <= maxWidth) return text;
        const ellipsis = "...";
        let truncated = text;
        while (truncated.length > 0 && font.widthOfTextAtSize(truncated + ellipsis, fontSize) > maxWidth) {
          truncated = truncated.slice(0, -1);
        }
        return truncated + ellipsis;
      };

      const drawHeader = (targetPage) => {
        targetPage.drawText(company.name?.toString() ?? "", { x: 35, y: 583, size: 16, font: calibriBold });
        targetPage.drawText(company.address?.toString() ?? "", { x: 35, y: 573, size: 9, font: calibriFont });
        targetPage.drawText(company.tin?.toString() ?? "", { x: 35, y: 563, size: 9, font: calibriFont });

        targetPage.drawText(`${monthFrom} to ${monthTo}`, { x: 83, y: 506, size: 9, font: calibriFont });
        targetPage.drawText(FormatDateToWord(new Date()), { x: 82, y: 495, size: 9, font: calibriFont });
      };

      let pages = pdfDoc.getPages();
      let currentPage = pages[0];
      drawHeader(currentPage);

      let yPosition = 446;
      const bottomThreshold = 80;
      const lineHeight = 20;

      // Helper to handle page breaks
      const addNewPage = async () => {
        const [templatePage] = await pdfDoc.copyPages(templateDoc, [0]);
        currentPage = pdfDoc.addPage(templatePage);
        drawHeader(currentPage);
        yPosition = 446;
      };

      if (yPosition - lineHeight < bottomThreshold) {
        await addNewPage();
      }
        
      currentPage.drawText(`${_data?.detailed?.revenue?.title ?? ""}`, { x: 40, y: yPosition, size: 9, font: calibriBold });
      yPosition -= 10;

      let revenueTotal = 0;
      let revenueTotalPercentage = 0;
      let revenueJanuary = 0;
      let revenueJanuaryPercentage = 0;
      let revenueFebruary = 0;
      let revenueFebruaryPercentage = 0;
      let revenueMarch = 0;
      let revenueMarchPercentage = 0;
      let revenueApril = 0;
      let revenueAprilPercentage = 0;
      let revenueMay = 0;
      let revenueMayPercentage = 0;
      let revenueJune = 0;
      let revenueJunePercentage = 0;
      let revenueJuly = 0;
      let revenueJulyPercentage = 0;
      let revenueAugust = 0;
      let revenueAugustPercentage = 0;
      let revenueSeptember = 0;
      let revenueSeptemberPercentage = 0;
      let revenueOctober = 0;
      let revenueOctoberPercentage = 0;
      let revenueNovember = 0;
      let revenueNovemberPercentage = 0;
      let revenueDecember = 0;
      let revenueDecemberPercentage = 0;

      for (const record of _data?.detailed?.revenue?.accounts) {
        // --- PAGE BREAK CHECK FOR ITEM ROW ---
        if (yPosition - lineHeight < bottomThreshold) {
          await addNewPage();
        }
        
        const truncatedAccount = truncateText(record?.accountName ?? "", 100, calibriFont, 8);
        currentPage.drawText(truncatedAccount, { x: 40, y: yPosition, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.total)}`, { x: 146, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.totalPercentage)} %`, { x: 146, y: yPosition - 10, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.monthly[0]?.amount)}`, { x: 187, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.monthly[0]?.percent)} %`, { x: 187, y: yPosition - 10, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.monthly[1]?.amount)}`, { x: 230, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.monthly[1]?.percent)} %`, { x: 230, y: yPosition - 10, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.monthly[2]?.amount)}`, { x: 277, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.monthly[2]?.percent)} %`, { x: 277, y: yPosition - 10, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.monthly[3]?.amount)}`, { x: 322, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.monthly[3]?.percent)} %`, { x: 322, y: yPosition - 10, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.monthly[4]?.amount)}`, { x: 366, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.monthly[4]?.percent)} %`, { x: 366, y: yPosition - 10, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.monthly[5]?.amount)}`, { x: 413, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.monthly[5]?.percent)} %`, { x: 413, y: yPosition - 10, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.monthly[6]?.amount)}`, { x: 457, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.monthly[6]?.percent)} %`, { x: 457, y: yPosition - 10, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.monthly[7]?.amount)}`, { x: 502, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.monthly[7]?.percent)} %`, { x: 502, y: yPosition - 10, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.monthly[8]?.amount)}`, { x: 552, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.monthly[8]?.percent)} %`, { x: 552, y: yPosition - 10, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.monthly[9]?.amount)}`, { x: 605, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.monthly[9]?.percent)} %`, { x: 605, y: yPosition - 10, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.monthly[10]?.amount)}`, { x: 655, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.monthly[10]?.percent)} %`, { x: 655, y: yPosition - 10, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.monthly[11]?.amount)}`, { x: 715, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.monthly[11]?.percent)} %`, { x: 715, y: yPosition - 10, size: 9, font: calibriFont });
        yPosition -= lineHeight;
        
        revenueTotal += record.total;
        revenueTotalPercentage += record.totalPercentage;
        revenueJanuary += record.monthly[0]?.amount;
        revenueJanuaryPercentage += record.monthly[0]?.percent;
        revenueFebruary += record.monthly[1]?.amount;
        revenueFebruaryPercentage += record.monthly[1]?.percent;
        revenueMarch += record.monthly[2]?.amount;
        revenueMarchPercentage += record.monthly[2]?.percent;
        revenueApril += record.monthly[3]?.amount;
        revenueAprilPercentage += record.monthly[3]?.percent;
        revenueMay += record.monthly[4]?.amount;
        revenueMayPercentage += record.monthly[4]?.percent;
        revenueJune += record.monthly[5]?.amount;
        revenueJunePercentage += record.monthly[5]?.percent;
        revenueJuly += record.monthly[6]?.amount;
        revenueJulyPercentage += record.monthly[6]?.percent;
        revenueAugust += record.monthly[7]?.amount;
        revenueAugustPercentage += record.monthly[7]?.percent;
        revenueSeptember += record.monthly[8]?.amount;
        revenueSeptemberPercentage += record.monthly[8]?.percent;
        revenueOctober += record.monthly[9]?.amount;
        revenueOctoberPercentage += record.monthly[9]?.percent;
        revenueNovember += record.monthly[10]?.amount;
        revenueNovemberPercentage += record.monthly[10]?.percent;
        revenueDecember += record.monthly[11]?.amount;
        revenueDecemberPercentage += record.monthly[11]?.percent;
      }

      // --- FOOTER SECTION (GRAND TOTAL) ---
      if (yPosition < 100) {
        await addNewPage();
      }

      currentPage.drawLine({ start: { x: 35, y: yPosition }, end: { x: 757, y: yPosition }, thickness: 1 });
      yPosition -= 15;
      currentPage.drawText(`Total ${_data?.detailed?.revenue?.title ?? ""}`, { x: 35, y: yPosition, size: 9, font: calibriBold });
      currentPage.drawText(`${FormatValueToDecimal(revenueTotal)}`, { x: 146, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(revenueTotalPercentage)} %`, { x: 146, y: yPosition - 10, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(revenueJanuary)}`, { x: 187, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(revenueJanuaryPercentage)} %`, { x: 187, y: yPosition - 10, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(revenueFebruary)}`, { x: 230, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(revenueFebruaryPercentage)} %`, { x: 230, y: yPosition - 10, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(revenueMarch)}`, { x: 277, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(revenueMarchPercentage)} %`, { x: 277, y: yPosition - 10, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(revenueApril)}`, { x: 322, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(revenueAprilPercentage)} %`, { x: 322, y: yPosition - 10, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(revenueMay)}`, { x: 366, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(revenueMayPercentage)} %`, { x: 366, y: yPosition - 10, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(revenueJune)}`, { x: 413, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(revenueJunePercentage)} %`, { x: 413, y: yPosition - 10, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(revenueJuly)}`, { x: 457, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(revenueJulyPercentage)} %`, { x: 457, y: yPosition - 10, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(revenueAugust)}`, { x: 502, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(revenueAugustPercentage)} %`, { x: 502, y: yPosition - 10, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(revenueSeptember)}`, { x: 552, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(revenueSeptemberPercentage)} %`, { x: 552, y: yPosition - 10, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(revenueOctober)}`, { x: 605, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(revenueOctoberPercentage)} %`, { x: 605, y: yPosition - 10, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(revenueNovember)}`, { x: 655, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(revenueNovemberPercentage)} %`, { x: 655, y: yPosition - 10, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(revenueDecember)}`, { x: 715, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(revenueDecemberPercentage)} %`, { x: 715, y: yPosition - 10, size: 9, font: calibriFont });
      yPosition -= 15;
      currentPage.drawLine({ start: { x: 35, y: yPosition }, end: { x: 757, y: yPosition }, thickness: 1 });
      yPosition -= 15;

      if (yPosition - lineHeight < bottomThreshold) {
        await addNewPage();
      }
        
      currentPage.drawText(`${_data?.detailed?.costOfSales?.title ?? ""}`, { x: 40, y: yPosition, size: 9, font: calibriBold });
      yPosition -= 10;

      let costOfSalesTotal = 0;
      let costOfSalesTotalPercentage = 0;
      let costOfSalesJanuary = 0;
      let costOfSalesJanuaryPercentage = 0;
      let costOfSalesFebruary = 0;
      let costOfSalesFebruaryPercentage = 0;
      let costOfSalesMarch = 0;
      let costOfSalesMarchPercentage = 0;
      let costOfSalesApril = 0;
      let costOfSalesAprilPercentage = 0;
      let costOfSalesMay = 0;
      let costOfSalesMayPercentage = 0;
      let costOfSalesJune = 0;
      let costOfSalesJunePercentage = 0;
      let costOfSalesJuly = 0;
      let costOfSalesJulyPercentage = 0;
      let costOfSalesAugust = 0;
      let costOfSalesAugustPercentage = 0;
      let costOfSalesSeptember = 0;
      let costOfSalesSeptemberPercentage = 0;
      let costOfSalesOctober = 0;
      let costOfSalesOctoberPercentage = 0;
      let costOfSalesNovember = 0;
      let costOfSalesNovemberPercentage = 0;
      let costOfSalesDecember = 0;
      let costOfSalesDecemberPercentage = 0;

      for (const record of _data?.detailed?.costOfSales?.accounts) {
        // --- PAGE BREAK CHECK FOR ITEM ROW ---
        if (yPosition - lineHeight < bottomThreshold) {
          await addNewPage();
        }
        
        const truncatedAccount = truncateText(record?.accountName ?? "", 100, calibriFont, 8);
        currentPage.drawText(truncatedAccount, { x: 40, y: yPosition, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.total)}`, { x: 146, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.totalPercentage)} %`, { x: 146, y: yPosition - 10, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.monthly[0]?.amount)}`, { x: 187, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.monthly[0]?.percent)} %`, { x: 187, y: yPosition - 10, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.monthly[1]?.amount)}`, { x: 230, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.monthly[1]?.percent)} %`, { x: 230, y: yPosition - 10, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.monthly[2]?.amount)}`, { x: 277, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.monthly[2]?.percent)} %`, { x: 277, y: yPosition - 10, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.monthly[3]?.amount)}`, { x: 322, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.monthly[3]?.percent)} %`, { x: 322, y: yPosition - 10, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.monthly[4]?.amount)}`, { x: 366, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.monthly[4]?.percent)} %`, { x: 366, y: yPosition - 10, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.monthly[5]?.amount)}`, { x: 413, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.monthly[5]?.percent)} %`, { x: 413, y: yPosition - 10, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.monthly[6]?.amount)}`, { x: 457, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.monthly[6]?.percent)} %`, { x: 457, y: yPosition - 10, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.monthly[7]?.amount)}`, { x: 502, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.monthly[7]?.percent)} %`, { x: 502, y: yPosition - 10, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.monthly[8]?.amount)}`, { x: 552, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.monthly[8]?.percent)} %`, { x: 552, y: yPosition - 10, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.monthly[9]?.amount)}`, { x: 605, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.monthly[9]?.percent)} %`, { x: 605, y: yPosition - 10, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.monthly[10]?.amount)}`, { x: 655, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.monthly[10]?.percent)} %`, { x: 655, y: yPosition - 10, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.monthly[11]?.amount)}`, { x: 715, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.monthly[11]?.percent)} %`, { x: 715, y: yPosition - 10, size: 9, font: calibriFont });
        yPosition -= lineHeight;
        
        costOfSalesTotal += record.total;
        costOfSalesTotalPercentage += record.totalPercentage;
        costOfSalesJanuary += record.monthly[0]?.amount;
        costOfSalesJanuaryPercentage += record.monthly[0]?.percent;
        costOfSalesFebruary += record.monthly[1]?.amount;
        costOfSalesFebruaryPercentage += record.monthly[1]?.percent;
        costOfSalesMarch += record.monthly[2]?.amount;
        costOfSalesMarchPercentage += record.monthly[2]?.percent;
        costOfSalesApril += record.monthly[3]?.amount;
        costOfSalesAprilPercentage += record.monthly[3]?.percent;
        costOfSalesMay += record.monthly[4]?.amount;
        costOfSalesMayPercentage += record.monthly[4]?.percent;
        costOfSalesJune += record.monthly[5]?.amount;
        costOfSalesJunePercentage += record.monthly[5]?.percent;
        costOfSalesJuly += record.monthly[6]?.amount;
        costOfSalesJulyPercentage += record.monthly[6]?.percent;
        costOfSalesAugust += record.monthly[7]?.amount;
        costOfSalesAugustPercentage += record.monthly[7]?.percent;
        costOfSalesSeptember += record.monthly[8]?.amount;
        costOfSalesSeptemberPercentage += record.monthly[8]?.percent;
        costOfSalesOctober += record.monthly[9]?.amount;
        costOfSalesOctoberPercentage += record.monthly[9]?.percent;
        costOfSalesNovember += record.monthly[10]?.amount;
        costOfSalesNovemberPercentage += record.monthly[10]?.percent;
        costOfSalesDecember += record.monthly[11]?.amount;
        costOfSalesDecemberPercentage += record.monthly[11]?.percent;
      }

      // --- FOOTER SECTION (GRAND TOTAL) ---
      if (yPosition < 100) {
        await addNewPage();
      }

      currentPage.drawLine({ start: { x: 35, y: yPosition }, end: { x: 757, y: yPosition }, thickness: 1 });
      yPosition -= 15;
      currentPage.drawText(`Total ${_data?.detailed?.costOfSales?.title ?? ""}`, { x: 35, y: yPosition, size: 9, font: calibriBold });
      currentPage.drawText(`${FormatValueToDecimal(costOfSalesTotal)}`, { x: 146, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(costOfSalesTotalPercentage)} %`, { x: 146, y: yPosition - 10, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(costOfSalesJanuary)}`, { x: 187, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(costOfSalesJanuaryPercentage)} %`, { x: 187, y: yPosition - 10, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(costOfSalesFebruary)}`, { x: 230, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(costOfSalesFebruaryPercentage)} %`, { x: 230, y: yPosition - 10, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(costOfSalesMarch)}`, { x: 277, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(costOfSalesMarchPercentage)} %`, { x: 277, y: yPosition - 10, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(costOfSalesApril)}`, { x: 322, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(costOfSalesAprilPercentage)} %`, { x: 322, y: yPosition - 10, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(costOfSalesMay)}`, { x: 366, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(costOfSalesMayPercentage)} %`, { x: 366, y: yPosition - 10, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(costOfSalesJune)}`, { x: 413, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(costOfSalesJunePercentage)} %`, { x: 413, y: yPosition - 10, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(costOfSalesJuly)}`, { x: 457, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(costOfSalesJulyPercentage)} %`, { x: 457, y: yPosition - 10, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(costOfSalesAugust)}`, { x: 502, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(costOfSalesAugustPercentage)} %`, { x: 502, y: yPosition - 10, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(costOfSalesSeptember)}`, { x: 552, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(costOfSalesSeptemberPercentage)} %`, { x: 552, y: yPosition - 10, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(costOfSalesOctober)}`, { x: 605, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(costOfSalesOctoberPercentage)} %`, { x: 605, y: yPosition - 10, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(costOfSalesNovember)}`, { x: 655, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(costOfSalesNovemberPercentage)} %`, { x: 655, y: yPosition - 10, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(costOfSalesDecember)}`, { x: 715, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(costOfSalesDecemberPercentage)} %`, { x: 715, y: yPosition - 10, size: 9, font: calibriFont });
      yPosition -= 15;
      currentPage.drawLine({ start: { x: 35, y: yPosition }, end: { x: 757, y: yPosition }, thickness: 1 });
      yPosition -= 15;

      if (yPosition - lineHeight < bottomThreshold) {
        await addNewPage();
      }
        
      currentPage.drawText(`${_data?.detailed?.expenses?.title ?? ""}`, { x: 40, y: yPosition, size: 9, font: calibriBold });
      yPosition -= 10;

      let expensesTotal = 0;
      let expensesTotalPercentage = 0;
      let expensesJanuary = 0;
      let expensesJanuaryPercentage = 0;
      let expensesFebruary = 0;
      let expensesFebruaryPercentage = 0;
      let expensesMarch = 0;
      let expensesMarchPercentage = 0;
      let expensesApril = 0;
      let expensesAprilPercentage = 0;
      let expensesMay = 0;
      let expensesMayPercentage = 0;
      let expensesJune = 0;
      let expensesJunePercentage = 0;
      let expensesJuly = 0;
      let expensesJulyPercentage = 0;
      let expensesAugust = 0;
      let expensesAugustPercentage = 0;
      let expensesSeptember = 0;
      let expensesSeptemberPercentage = 0;
      let expensesOctober = 0;
      let expensesOctoberPercentage = 0;
      let expensesNovember = 0;
      let expensesNovemberPercentage = 0;
      let expensesDecember = 0;
      let expensesDecemberPercentage = 0;

      for (const record of _data?.detailed?.expenses?.accounts) {
        // --- PAGE BREAK CHECK FOR ITEM ROW ---
        if (yPosition - lineHeight < bottomThreshold) {
          await addNewPage();
        }
        
        const truncatedAccount = truncateText(record?.accountName ?? "", 100, calibriFont, 8);
        currentPage.drawText(truncatedAccount, { x: 40, y: yPosition, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.total)}`, { x: 146, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.totalPercentage)} %`, { x: 146, y: yPosition - 10, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.monthly[0]?.amount)}`, { x: 187, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.monthly[0]?.percent)} %`, { x: 187, y: yPosition - 10, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.monthly[1]?.amount)}`, { x: 230, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.monthly[1]?.percent)} %`, { x: 230, y: yPosition - 10, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.monthly[2]?.amount)}`, { x: 277, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.monthly[2]?.percent)} %`, { x: 277, y: yPosition - 10, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.monthly[3]?.amount)}`, { x: 322, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.monthly[3]?.percent)} %`, { x: 322, y: yPosition - 10, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.monthly[4]?.amount)}`, { x: 366, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.monthly[4]?.percent)} %`, { x: 366, y: yPosition - 10, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.monthly[5]?.amount)}`, { x: 413, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.monthly[5]?.percent)} %`, { x: 413, y: yPosition - 10, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.monthly[6]?.amount)}`, { x: 457, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.monthly[6]?.percent)} %`, { x: 457, y: yPosition - 10, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.monthly[7]?.amount)}`, { x: 502, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.monthly[7]?.percent)} %`, { x: 502, y: yPosition - 10, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.monthly[8]?.amount)}`, { x: 552, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.monthly[8]?.percent)} %`, { x: 552, y: yPosition - 10, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.monthly[9]?.amount)}`, { x: 605, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.monthly[9]?.percent)} %`, { x: 605, y: yPosition - 10, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.monthly[10]?.amount)}`, { x: 655, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.monthly[10]?.percent)} %`, { x: 655, y: yPosition - 10, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.monthly[11]?.amount)}`, { x: 715, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.monthly[11]?.percent)} %`, { x: 715, y: yPosition - 10, size: 9, font: calibriFont });
        yPosition -= lineHeight;
        
        expensesTotal += record.total;
        expensesTotalPercentage += record.totalPercentage;
        expensesJanuary += record.monthly[0]?.amount;
        expensesJanuaryPercentage += record.monthly[0]?.percent;
        expensesFebruary += record.monthly[1]?.amount;
        expensesFebruaryPercentage += record.monthly[1]?.percent;
        expensesMarch += record.monthly[2]?.amount;
        expensesMarchPercentage += record.monthly[2]?.percent;
        expensesApril += record.monthly[3]?.amount;
        expensesAprilPercentage += record.monthly[3]?.percent;
        expensesMay += record.monthly[4]?.amount;
        expensesMayPercentage += record.monthly[4]?.percent;
        expensesJune += record.monthly[5]?.amount;
        expensesJunePercentage += record.monthly[5]?.percent;
        expensesJuly += record.monthly[6]?.amount;
        expensesJulyPercentage += record.monthly[6]?.percent;
        expensesAugust += record.monthly[7]?.amount;
        expensesAugustPercentage += record.monthly[7]?.percent;
        expensesSeptember += record.monthly[8]?.amount;
        expensesSeptemberPercentage += record.monthly[8]?.percent;
        expensesOctober += record.monthly[9]?.amount;
        expensesOctoberPercentage += record.monthly[9]?.percent;
        expensesNovember += record.monthly[10]?.amount;
        expensesNovemberPercentage += record.monthly[10]?.percent;
        expensesDecember += record.monthly[11]?.amount;
        expensesDecemberPercentage += record.monthly[11]?.percent;
      }

      // --- FOOTER SECTION (GRAND TOTAL) ---
      if (yPosition < 100) {
        await addNewPage();
      }

      currentPage.drawLine({ start: { x: 35, y: yPosition }, end: { x: 757, y: yPosition }, thickness: 1 });
      yPosition -= 15;
      currentPage.drawText(`Total ${_data?.detailed?.expenses?.title ?? ""}`, { x: 35, y: yPosition, size: 9, font: calibriBold });
      currentPage.drawText(`${FormatValueToDecimal(expensesTotal)}`, { x: 146, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(expensesTotalPercentage)} %`, { x: 146, y: yPosition - 10, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(expensesJanuary)}`, { x: 187, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(expensesJanuaryPercentage)} %`, { x: 187, y: yPosition - 10, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(expensesFebruary)}`, { x: 230, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(expensesFebruaryPercentage)} %`, { x: 230, y: yPosition - 10, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(expensesMarch)}`, { x: 277, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(expensesMarchPercentage)} %`, { x: 277, y: yPosition - 10, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(expensesApril)}`, { x: 322, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(expensesAprilPercentage)} %`, { x: 322, y: yPosition - 10, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(expensesMay)}`, { x: 366, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(expensesMayPercentage)} %`, { x: 366, y: yPosition - 10, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(expensesJune)}`, { x: 413, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(expensesJunePercentage)} %`, { x: 413, y: yPosition - 10, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(expensesJuly)}`, { x: 457, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(expensesJulyPercentage)} %`, { x: 457, y: yPosition - 10, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(expensesAugust)}`, { x: 502, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(expensesAugustPercentage)} %`, { x: 502, y: yPosition - 10, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(expensesSeptember)}`, { x: 552, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(expensesSeptemberPercentage)} %`, { x: 552, y: yPosition - 10, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(expensesOctober)}`, { x: 605, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(expensesOctoberPercentage)} %`, { x: 605, y: yPosition - 10, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(expensesNovember)}`, { x: 655, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(expensesNovemberPercentage)} %`, { x: 655, y: yPosition - 10, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(expensesDecember)}`, { x: 715, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(expensesDecemberPercentage)} %`, { x: 715, y: yPosition - 10, size: 9, font: calibriFont });
      yPosition -= 15;

      // --- PAGINATION ---
      const allPages = pdfDoc.getPages();
      const totalPages = allPages.length;
      allPages.forEach((page, index) => {
        page.drawText(`Page ${index + 1} of ${totalPages}`, {
          x: 680,
          y: 580,
          size: 10,
          font: calibriBold,
        });
      });

      const modifiedPdfBytes = await pdfDoc.save();
      const blob = new Blob([modifiedPdfBytes], { type: "application/pdf" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `IS_DETAILED.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

    } catch (error) {
      console.error("Error:", error);
      setFailMessage("Error processing PDF");
    }
    setLoading(false);
  };

  const generateISSummarized = async () => {
    if (!monthFrom || !monthTo || !year || !company) {
      setFailMessage("Please fill up all fields");
      return;
    }

    setLoading(true);
    const allData = await fetchReportData();
    const _data = allData.incomeStatement;

    if (!_data || _data?.length <= 0) {
      setFailMessage("No record found");
      setLoading(false);
      return;
    }

    try {
      const pdfUrl = `${window.location.origin}/docs/IS_SUMMARIZED.pdf`;
      const templateBytes = await fetch(pdfUrl).then((r) => r.arrayBuffer());
      const templateDoc = await PDFDocument.load(templateBytes);
      
      const response = await fetch(pdfUrl);
      const pdfBytes = await response.arrayBuffer();
      const pdfDoc = await PDFDocument.load(pdfBytes);

      pdfDoc.registerFontkit(fontkit);
      const fontResponse = await fetch("/calibri.ttf");
      const fontBytes = await fontResponse.arrayBuffer();
      const calibriFont = await pdfDoc.embedFont(fontBytes);

      const boldResponse = await fetch("/calibrib.ttf");
      const boldBytes = await boldResponse.arrayBuffer();
      const calibriBold = await pdfDoc.embedFont(boldBytes);

      const drawHeader = (targetPage) => {
        targetPage.drawText(company.name?.toString() ?? "", { x: 35, y: 756, size: 16, font: calibriBold });
        targetPage.drawText(company.address?.toString() ?? "", { x: 35, y: 746, size: 9, font: calibriFont });
        targetPage.drawText(company.tin?.toString() ?? "", { x: 35, y: 736, size: 9, font: calibriFont });

        targetPage.drawText(`${year}`, { x: 90, y: 695, size: 9, font: calibriFont });
        targetPage.drawText(FormatDateToWord(new Date()), { x: 82, y: 685.5, size: 9, font: calibriFont });

        targetPage.drawText(`${year}`, { x: 345, y: 654, size: 9, font: calibriFont });
      };

      let pages = pdfDoc.getPages();
      let currentPage = pages[0];
      drawHeader(currentPage);

      let yPosition = 630;
      // const bottomThreshold = 80;
      const lineHeight = 12;

      // Helper to handle page breaks
      const addNewPage = async () => {
        const [templatePage] = await pdfDoc.copyPages(templateDoc, [0]);
        currentPage = pdfDoc.addPage(templatePage);
        drawHeader(currentPage);
        yPosition = 630;
      };

      const value = _data.summary;

      currentPage.drawText(`REVENUE`, { x: 40, y: yPosition, size: 9, font: calibriBold });
      currentPage.drawText(`${FormatValueToDecimal(value.revenue)}`, { x: 320, y: yPosition, size: 9, font: calibriFont });
      yPosition -= lineHeight

      currentPage.drawText(`DIRECT COST`, { x: 40, y: yPosition, size: 9, font: calibriBold });
      currentPage.drawText(`${FormatValueToDecimal(value.directCost)}`, { x: 320, y: yPosition, size: 9, font: calibriFont });
      yPosition -= lineHeight

      var grossProfit =  value.revenue + value.directCost

      currentPage.drawLine({ start: { x: 35, y: yPosition }, end: { x: 590, y: yPosition }, thickness: 1 });
      yPosition -= lineHeight;
      currentPage.drawText(`GROSS PROFIT`, { x: 40, y: yPosition, size: 9, font: calibriBold });
      currentPage.drawText(`${FormatValueToDecimal(grossProfit)}`, { x: 320, y: yPosition, size: 9, font: calibriFont });
      yPosition -= lineHeight;

      currentPage.drawText(`ADMINISTRATIVE EXPENSES`, { x: 40, y: yPosition, size: 9, font: calibriBold });
      currentPage.drawText(`${FormatValueToDecimal(value.administrativeExpenses)}`, { x: 320, y: yPosition, size: 9, font: calibriFont });
      yPosition -= lineHeight

      currentPage.drawText(`OTHER INCOME`, { x: 40, y: yPosition, size: 9, font: calibriBold });
      currentPage.drawText(`${FormatValueToDecimal(value.otherIncome)}`, { x: 320, y: yPosition, size: 9, font: calibriFont });
      yPosition -= lineHeight

      var netOperatingIncome = grossProfit + value.administrativeExpenses + value.otherIncome

      currentPage.drawLine({ start: { x: 35, y: yPosition }, end: { x: 590, y: yPosition }, thickness: 1 });
      yPosition -= lineHeight;
      currentPage.drawText(`NET OPERATING INCOME`, { x: 40, y: yPosition, size: 9, font: calibriBold });
      currentPage.drawText(`${FormatValueToDecimal(netOperatingIncome )}`, { x: 320, y: yPosition, size: 9, font: calibriFont });
      yPosition -= lineHeight;

      currentPage.drawText(`PROVISION FOR INCOME TAX`, { x: 40, y: yPosition, size: 9, font: calibriBold });
      currentPage.drawText(`${FormatValueToDecimal(value.provisionForIncomeTax)}`, { x: 320, y: yPosition, size: 9, font: calibriFont });
      yPosition -= lineHeight

      var totalComprehensiveIncome = netOperatingIncome + value.provisionForIncomeTax

      currentPage.drawLine({ start: { x: 35, y: yPosition }, end: { x: 590, y: yPosition }, thickness: 1 });
      yPosition -= lineHeight;
      currentPage.drawText(`TOTAL COMPREHENSIVE INCOME`, { x: 40, y: yPosition, size: 9, font: calibriBold });
      currentPage.drawText(`${FormatValueToDecimal(totalComprehensiveIncome )}`, { x: 320, y: yPosition, size: 9, font: calibriFont });
      yPosition -= lineHeight;

      // --- FOOTER SECTION (GRAND TOTAL) ---
      if (yPosition < 100) {
        await addNewPage();
      }

      // --- PAGINATION ---
      const allPages = pdfDoc.getPages();
      const totalPages = allPages.length;
      allPages.forEach((page, index) => {
        page.drawText(`Page ${index + 1} of ${totalPages}`, {
          x: 680,
          y: 580,
          size: 10,
          font: calibriBold,
        });
      });

      const modifiedPdfBytes = await pdfDoc.save();
      const blob = new Blob([modifiedPdfBytes], { type: "application/pdf" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `IS_SUMMARIZED.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

    } catch (error) {
      console.error("Error:", error);
      setFailMessage("Error processing PDF");
    }
    setLoading(false);
  };

  const generateISSummarizedPercentage = async () => {
    if (!monthFrom || !monthTo || !year || !company) {
      setFailMessage("Please fill up all fields");
      return;
    }

    setLoading(true);
    const allData = await fetchReportData();
    const _data = allData.incomeStatement;

    if (!_data || _data?.length <= 0) {
      setFailMessage("No record found");
      setLoading(false);
      return;
    }

    try {
      const pdfUrl = `${window.location.origin}/docs/IS_SUMMARIZED_PERCENTAGE.pdf`;
      const templateBytes = await fetch(pdfUrl).then((r) => r.arrayBuffer());
      const templateDoc = await PDFDocument.load(templateBytes);
      
      const response = await fetch(pdfUrl);
      const pdfBytes = await response.arrayBuffer();
      const pdfDoc = await PDFDocument.load(pdfBytes);

      pdfDoc.registerFontkit(fontkit);
      const fontResponse = await fetch("/calibri.ttf");
      const fontBytes = await fontResponse.arrayBuffer();
      const calibriFont = await pdfDoc.embedFont(fontBytes);

      const boldResponse = await fetch("/calibrib.ttf");
      const boldBytes = await boldResponse.arrayBuffer();
      const calibriBold = await pdfDoc.embedFont(boldBytes);

      const drawHeader = (targetPage) => {
        targetPage.drawText(company.name?.toString() ?? "", { x: 35, y: 756, size: 16, font: calibriBold });
        targetPage.drawText(company.address?.toString() ?? "", { x: 35, y: 746, size: 9, font: calibriFont });
        targetPage.drawText(company.tin?.toString() ?? "", { x: 35, y: 736, size: 9, font: calibriFont });

        targetPage.drawText(`${year}`, { x: 90, y: 695, size: 9, font: calibriFont });
        targetPage.drawText(FormatDateToWord(new Date()), { x: 82, y: 685.5, size: 9, font: calibriFont });

        targetPage.drawText(`${year}`, { x: 345, y: 654, size: 9, font: calibriFont });
      };

      const truncateText = (text, maxWidth, font, fontSize) => {
        if (!text) return "";
        let currentWidth = font.widthOfTextAtSize(text, fontSize);
        if (currentWidth <= maxWidth) return text;
        const ellipsis = "...";
        let truncated = text;
        while (truncated.length > 0 && font.widthOfTextAtSize(truncated + ellipsis, fontSize) > maxWidth) {
          truncated = truncated.slice(0, -1);
        }
        return truncated + ellipsis;
      };

      let pages = pdfDoc.getPages();
      let currentPage = pages[0];
      drawHeader(currentPage);

      let yPosition = 630;
      const bottomThreshold = 80;
      const lineHeight = 12;

      // Helper to handle page breaks
      const addNewPage = async () => {
        const [templatePage] = await pdfDoc.copyPages(templateDoc, [0]);
        currentPage = pdfDoc.addPage(templatePage);
        drawHeader(currentPage);
        yPosition = 630;
      };
      
      let revenueAmount = 0;
      let revenuePercentage = 0;

      currentPage.drawText(`${_data?.summaryPercentage?.revenue?.title ?? ""}`, { x: 40, y: yPosition, size: 9, font: calibriBold });
      yPosition -= 10;

      for (const record of _data?.summaryPercentage?.revenue?.accounts) {
        // --- PAGE BREAK CHECK FOR ITEM ROW ---
        if (yPosition - lineHeight < bottomThreshold) {
          await addNewPage();
        }

        const truncatedAccount = truncateText(record?.accountName ?? "", 120, calibriFont, 8);
        currentPage.drawText(truncatedAccount, { x: 40, y: yPosition, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.amount)}`, { x: 320, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.percentage)} %`, { x: 447, y: yPosition, size: 9, font: calibriFont });
        yPosition -= lineHeight;

        revenueAmount += record.amount;
        revenuePercentage += record.percentage;
      }

      // --- FOOTER SECTION (GRAND TOTAL) ---
      if (yPosition < 100) {
        await addNewPage();
      }

      currentPage.drawLine({ start: { x: 35, y: yPosition }, end: { x: 590, y: yPosition }, thickness: 1 });
      yPosition -= lineHeight;
      currentPage.drawText(`Total ${_data?.summaryPercentage?.revenue?.title ?? ""}`, { x: 35, y: yPosition, size: 9, font: calibriBold });
      currentPage.drawText(`${FormatValueToDecimal(revenueAmount)}`, { x: 320, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(revenuePercentage)} %`, { x: 447, y: yPosition, size: 9, font: calibriFont });
      yPosition -= 5;
      currentPage.drawLine({ start: { x: 35, y: yPosition }, end: { x: 590, y: yPosition }, thickness: 1 });
      yPosition -= lineHeight;

      let costOfSalesAmount = 0;
      let costOfSalesPercentage = 0;

      currentPage.drawText(`${_data?.summaryPercentage?.costOfSales?.title ?? ""}`, { x: 40, y: yPosition, size: 9, font: calibriBold });
      yPosition -= 10;

      for (const record of _data?.summaryPercentage?.costOfSales?.accounts) {
        // --- PAGE BREAK CHECK FOR ITEM ROW ---
        if (yPosition - lineHeight < bottomThreshold) {
          await addNewPage();
        }

        const truncatedAccount = truncateText(record?.accountName ?? "", 120, calibriFont, 8);
        currentPage.drawText(truncatedAccount, { x: 40, y: yPosition, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.amount)}`, { x: 320, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.percentage)} %`, { x: 447, y: yPosition, size: 9, font: calibriFont });
        yPosition -= lineHeight;

        costOfSalesAmount += record.amount;
        costOfSalesPercentage += record.percentage;
      }

      // --- FOOTER SECTION (GRAND TOTAL) ---
      if (yPosition < 100) {
        await addNewPage();
      }

      currentPage.drawLine({ start: { x: 35, y: yPosition }, end: { x: 590, y: yPosition }, thickness: 1 });
      yPosition -= lineHeight;
      currentPage.drawText(`Total ${_data?.summaryPercentage?.costOfSales?.title ?? ""}`, { x: 35, y: yPosition, size: 9, font: calibriBold });
      currentPage.drawText(`${FormatValueToDecimal(costOfSalesAmount)}`, { x: 320, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(costOfSalesPercentage)} %`, { x: 447, y: yPosition, size: 9, font: calibriFont });
      yPosition -= 5;
      currentPage.drawLine({ start: { x: 35, y: yPosition }, end: { x: 590, y: yPosition }, thickness: 1 });
      yPosition -= lineHeight;

      let expensesAmount = 0;
      let expensesPercentage = 0;

      currentPage.drawText(`${_data?.summaryPercentage?.expenses?.title ?? ""}`, { x: 40, y: yPosition, size: 9, font: calibriBold });
      yPosition -= 10;

      for (const record of _data?.summaryPercentage?.expenses?.accounts) {
        // --- PAGE BREAK CHECK FOR ITEM ROW ---
        if (yPosition - lineHeight < bottomThreshold) {
          await addNewPage();
        }

        const truncatedAccount = truncateText(record?.accountName ?? "", 120, calibriFont, 8);
        currentPage.drawText(truncatedAccount, { x: 40, y: yPosition, size: 9, font: calibriFont });

        currentPage.drawText(`${FormatValueToDecimal(record.amount)}`, { x: 320, y: yPosition, size: 9, font: calibriFont });
        currentPage.drawText(`${FormatValueToDecimal(record.percentage)} %`, { x: 447, y: yPosition, size: 9, font: calibriFont });
        yPosition -= lineHeight;

        expensesAmount += record.amount;
        expensesPercentage += record.percentage;
      }

      // --- FOOTER SECTION (GRAND TOTAL) ---
      if (yPosition < 100) {
        await addNewPage();
      }

      currentPage.drawLine({ start: { x: 35, y: yPosition }, end: { x: 590, y: yPosition }, thickness: 1 });
      yPosition -= lineHeight;
      currentPage.drawText(`Total ${_data?.summaryPercentage?.expenses?.title ?? ""}`, { x: 35, y: yPosition, size: 9, font: calibriBold });
      currentPage.drawText(`${FormatValueToDecimal(expensesAmount)}`, { x: 320, y: yPosition, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(expensesPercentage)} %`, { x: 447, y: yPosition, size: 9, font: calibriFont });
      yPosition -= 5;
      currentPage.drawLine({ start: { x: 35, y: yPosition }, end: { x: 590, y: yPosition }, thickness: 1 });
      yPosition -= lineHeight;

      // --- PAGINATION ---
      const allPages = pdfDoc.getPages();
      const totalPages = allPages.length;
      allPages.forEach((page, index) => {
        page.drawText(`Page ${index + 1} of ${totalPages}`, {
          x: 680,
          y: 580,
          size: 10,
          font: calibriBold,
        });
      });

      const modifiedPdfBytes = await pdfDoc.save();
      const blob = new Blob([modifiedPdfBytes], { type: "application/pdf" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `IS_SUMMARIZED_PERCENTAGE.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

    } catch (error) {
      console.error("Error:", error);
      setFailMessage("Error processing PDF");
    }
    setLoading(false);
  };

  const generateCFSummarized = async () => {
    if (!monthFrom || !monthTo || !year || !company) {
      setFailMessage("Please fill up all fields");
      return;
    }

    setLoading(true);
    const allData = await fetchReportData();
    const _data = allData.cashFlow;

    if (!_data || _data?.length <= 0) {
      setFailMessage("No record found");
      setLoading(false);
      return;
    }

    try {
      const pdfUrl = `${window.location.origin}/docs/CASH_FLOW.pdf`;
      // const templateBytes = await fetch(pdfUrl).then((r) => r.arrayBuffer());
      // const templateDoc = await PDFDocument.load(templateBytes);
      
      const response = await fetch(pdfUrl);
      const pdfBytes = await response.arrayBuffer();
      const pdfDoc = await PDFDocument.load(pdfBytes);

      pdfDoc.registerFontkit(fontkit);
      const fontResponse = await fetch("/calibri.ttf");
      const fontBytes = await fontResponse.arrayBuffer();
      const calibriFont = await pdfDoc.embedFont(fontBytes);

      const boldResponse = await fetch("/calibrib.ttf");
      const boldBytes = await boldResponse.arrayBuffer();
      const calibriBold = await pdfDoc.embedFont(boldBytes);

      const drawHeader = (targetPage) => {
        targetPage.drawText(company.name?.toString() ?? "", { x: 35, y: 746, size: 16, font: calibriBold });
        targetPage.drawText(company.address?.toString() ?? "", { x: 35, y: 730, size: 9, font: calibriFont });
        targetPage.drawText(company.tin?.toString() ?? "", { x: 35, y: 716, size: 9, font: calibriFont });

        targetPage.drawText(`${monthFrom} - ${monthTo} of ${year}`, { x: 110, y: 655, size: 9, font: calibriFont });
        targetPage.drawText(FormatDateToWord(new Date()), { x: 95, y: 636.5, size: 9, font: calibriFont });
      };

      let pages = pdfDoc.getPages();
      let currentPage = pages[0];
      drawHeader(currentPage);

      // let yPosition = 630;
      // const bottomThreshold = 80;
      // const lineHeight = 12;

      // Helper to handle page breaks
      /* const addNewPage = async () => {
        const [templatePage] = await pdfDoc.copyPages(templateDoc, [0]);
        currentPage = pdfDoc.addPage(templatePage);
        drawHeader(currentPage);
        yPosition = 630;
      }; */

      currentPage.drawText(`${FormatValueToDecimal(_data.operatingActivities?.netIncome)}`, { x: 450, y: 581, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(_data.operatingActivities?.depreciation)}`, { x: 450, y: 543, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(_data.operatingActivities?.changeInventory)}`, { x: 450, y: 509, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(_data.operatingActivities?.changeEmployeeAdvances)}`, { x: 450, y: 490, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(_data.operatingActivities?.changeReceivables)}`, { x: 450, y: 471, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(_data.operatingActivities?.changeOtherAssets)}`, { x: 450, y: 452, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(_data.operatingActivities?.changeCurrentLiabilities)}`, { x: 450, y: 433, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(_data.operatingActivities?.totalOperating)}`, { x: 450, y: 418, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(_data.investingActivities?.purchaseEquipment)}`, { x: 450, y: 363, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(_data.investingActivities?.purchaseBuilding)}`, { x: 450, y: 345, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(_data.investingActivities?.purchaseRenovations)}`, { x: 450, y: 328, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(_data.investingActivities?.totalInvesting)}`, { x: 450, y: 310, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(_data.financingActivities?.proceedsLoan)}`, { x: 450, y: 234, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(_data.financingActivities?.proceedsShareholders)}`, { x: 450, y: 215, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(_data.financingActivities?.issuanceShareCapital)}`, { x: 450, y: 198, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(_data.financingActivities?.totalFinancing)}`, { x: 450, y: 180, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(_data.netIncreaseDecrease)}`, { x: 450, y: 143, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(_data.cashBeginning)}`, { x: 450, y: 125, size: 9, font: calibriFont });
      currentPage.drawText(`${FormatValueToDecimal(_data.cashEnding)}`, { x: 450, y: 107, size: 9, font: calibriFont });

      currentPage.drawText(`${monthFrom} 31, ${year}`, { x: 153, y: 125, size: 9, font: calibriFont });
      currentPage.drawText(`${monthTo} 31, ${year}`, { x: 128, y: 107, size: 9, font: calibriFont });

      currentPage.drawText(`${FormatValueToDecimal(_data.cashEnding)}`, { x: 450, y: 68, size: 9, font: calibriFont });

      // --- PAGINATION ---
      const allPages = pdfDoc.getPages();
      const totalPages = allPages.length;
      allPages.forEach((page, index) => {
        page.drawText(`Page ${index + 1} of ${totalPages}`, {
          x: 680,
          y: 580,
          size: 10,
          font: calibriBold,
        });
      });

      const modifiedPdfBytes = await pdfDoc.save();
      const blob = new Blob([modifiedPdfBytes], { type: "application/pdf" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `CASH_FLOW.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

    } catch (error) {
      console.error("Error:", error);
      setFailMessage("Error processing PDF");
    }
    setLoading(false);
  };

  const exportBSDetailedExcel = async () => {
    if (!monthFrom || !monthTo || !year || !company) {
      setFailMessage("Please fill up all fields");
      return;
    }

    setLoading(true);

    try {
      const allData = await fetchReportData();
      const _data = allData.balanceSheet?.detailed;

      if (!_data || _data?.length <= 0) {
        setFailMessage("No record found");
        setLoading(false);
        return;
      }

      // 1. Create Workbook and Worksheet
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Detailed Balance Sheet');

      // 2. Add Company Header Information
      worksheet.addRow([company.name?.toUpperCase()]).font = { bold: true, size: 14 };
      worksheet.addRow([company.address]);
      worksheet.addRow([`TIN: ${company.tin}`]);
      worksheet.addRow([]); // Gap

      // 3. Add Report Title and Filters
      worksheet.addRow(['DETAILED BALANCE SHEET']).font = { bold: true, size: 12 };
      worksheet.addRow([`Period: ${monthFrom} to ${monthTo} ${year}`]);
      worksheet.addRow([`Date Printed: ${FormatDateToWord(new Date())}`]);
      worksheet.addRow([]); // Gap

      // 4. Define Table Columns
      const headerRow = worksheet.addRow([
        'Account',
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December'
      ]);

      // Style the Header Row
      headerRow.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: 'FFFFFF' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '444444' } };
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
        cell.border = { 
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' } 
        };
      });

      // 5. Add Data Grouped by Account Type
      _data.forEach(category => {
        // Add Category Title Row (e.g., "Current Assets")
        const typeRow = worksheet.addRow([category.accountType?.toUpperCase()]);
        typeRow.font = { bold: true };
        typeRow.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'F2F2F2' } };

        // Add individual accounts under this category
        category.accounts.forEach(acc => {
          const row = worksheet.addRow([
            acc.glAccountName ?? "N/A",
            parseFloat(acc.january || 0),
            parseFloat(acc.february || 0),
            parseFloat(acc.march || 0),
            parseFloat(acc.april || 0),
            parseFloat(acc.may || 0),
            parseFloat(acc.june || 0),
            parseFloat(acc.july || 0),
            parseFloat(acc.august || 0),
            parseFloat(acc.september || 0),
            parseFloat(acc.october || 0),
            parseFloat(acc.november || 0),
            parseFloat(acc.december || 0),
          ]);

          // Numeric formatting for columns B to M
          for (let i = 2; i <= 13; i++) {
            row.getCell(i).numFmt = '#,##0.00';
          }
        });

        // Add Category Totals Row
        const subTotalRow = worksheet.addRow([
          `Total ${category.accountType}`,
          parseFloat(category.totals?.january || 0),
          parseFloat(category.totals?.february || 0),
          parseFloat(category.totals?.march || 0),
          parseFloat(category.totals?.april || 0),
          parseFloat(category.totals?.may || 0),
          parseFloat(category.totals?.june || 0),
          parseFloat(category.totals?.july || 0),
          parseFloat(category.totals?.august || 0),
          parseFloat(category.totals?.september || 0),
          parseFloat(category.totals?.october || 0),
          parseFloat(category.totals?.november || 0),
          parseFloat(category.totals?.december || 0),
        ]);

        subTotalRow.font = { bold: true };
        for (let i = 1; i <= 13; i++) {
          const cell = subTotalRow.getCell(i);
          if (i > 1) cell.numFmt = '#,##0.00';
          cell.border = { top: { style: 'thin' }, bottom: { style: 'thin' } };
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'E9ECEF' } };
        }
        
        worksheet.addRow([]); // Add small gap between categories
      });

      // 6. Adjust Column Widths
      worksheet.columns = [
        { width: 40 }, // Account Name
        { width: 14 }, { width: 14 }, { width: 14 }, { width: 14 }, 
        { width: 14 }, { width: 14 }, { width: 14 }, { width: 14 }, 
        { width: 14 }, { width: 14 }, { width: 14 }, { width: 14 }, 
      ];

      // 7. Generate and Download
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const filename = `Detailed_Balance_Sheet_${year}.xlsx`;
      
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      link.click();
      URL.revokeObjectURL(link.href);

    } catch (error) {
      console.error("Excel Error:", error);
      setFailMessage("Error generating Excel file");
    }

    setLoading(false);
  };

  const exportBSSummarizedExcel = async () => {
    if (!monthFrom || !monthTo || !year || !company) {
      setFailMessage("Please fill up all fields");
      return;
    }

    setLoading(true);

    try {
      const allData = await fetchReportData();
      const _data = allData.balanceSheet?.summary;

      if (!_data || _data?.length <= 0) {
        setFailMessage("No record found");
        setLoading(false);
        return;
      }

      // 1. Create Workbook and Worksheet
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Summarized Balance Sheet');

      // 2. Add Company Header Information
      worksheet.addRow([company.name?.toUpperCase()]).font = { bold: true, size: 14 };
      worksheet.addRow([company.address]);
      worksheet.addRow([`TIN: ${company.tin}`]);
      worksheet.addRow([]); // Gap

      // 3. Add Report Title and Filters
      worksheet.addRow(['SUMMARIZED BALANCE SHEET']).font = { bold: true, size: 12 };
      worksheet.addRow([`As of Year: ${year}`]);
      worksheet.addRow([`Date Printed: ${FormatDateToWord(new Date())}`]);
      worksheet.addRow([]); // Gap

      // 4. Define Table Columns
      // Using the year variable dynamically for the header
      const headerRow = worksheet.addRow([
        'Account Description',
        `Amount (${year})`
      ]);

      // Style the Header Row
      headerRow.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: 'FFFFFF' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '444444' } };
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
        cell.border = { 
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' } 
        };
      });

      // 5. Add Data Rows
      _data.forEach(category => {
        // Add Category Title Row (e.g., "ASSETS")
        const typeRow = worksheet.addRow([category.accountType?.toUpperCase(), ""]);
        typeRow.font = { bold: true };
        typeRow.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'F2F2F2' } };

        // Add individual accounts
        category.accounts.forEach(acc => {
          // Based on your PDF logic, we take the first item in amountsByYear (index 0)
          const currentYearAmount = acc.amountsByYear && acc.amountsByYear.length > 0 
            ? parseFloat(acc.amountsByYear[0].amount || 0) 
            : 0;

          const row = worksheet.addRow([
            acc.glAccountName ?? "N/A",
            currentYearAmount
          ]);

          // Format numeric cell
          row.getCell(2).numFmt = '#,##0.00';
          row.getCell(2).alignment = { horizontal: 'right' };
        });

        // Add Category Totals Row
        const categoryTotal = category.totals && category.totals.length > 0 
          ? parseFloat(category.totals[0].totalAmount || 0) 
          : 0;

        const subTotalRow = worksheet.addRow([
          `Total ${category.accountType}`,
          categoryTotal
        ]);

        subTotalRow.font = { bold: true };
        subTotalRow.getCell(2).numFmt = '#,##0.00';
        subTotalRow.getCell(2).alignment = { horizontal: 'right' };
        
        // Styling the subtotal row
        subTotalRow.eachCell((cell) => {
          cell.border = { top: { style: 'thin' }, bottom: { style: 'double' } };
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'E9ECEF' } };
        });

        worksheet.addRow([]); // Gap between sections
      });

      // 6. Adjust Column Widths
      worksheet.columns = [
        { width: 50 }, // Account Description
        { width: 25 }, // Amount
      ];

      // 7. Generate and Download
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const filename = `Summarized_Balance_Sheet_${year}.xlsx`;
      
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      link.click();
      URL.revokeObjectURL(link.href);

    } catch (error) {
      console.error("Excel Error:", error);
      setFailMessage("Error generating Excel file");
    }

    setLoading(false);
  };

  const exportISDetailedExcel = async () => {
    if (!monthFrom || !monthTo || !year || !company) {
      setFailMessage("Please fill up all fields");
      return;
    }

    setLoading(true);

    try {
      const allData = await fetchReportData();
      const _data = allData.incomeStatement?.detailed;

      if (!_data) {
        setFailMessage("No record found");
        setLoading(false);
        return;
      }

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Detailed Income Statement');

      // 1. Company Headers
      worksheet.addRow([company.name?.toUpperCase()]).font = { bold: true, size: 14 };
      worksheet.addRow([company.address]);
      worksheet.addRow([`TIN: ${company.tin}`]);
      worksheet.addRow([]); 

      // 2. Title
      worksheet.addRow(['DETAILED INCOME STATEMENT']).font = { bold: true, size: 12 };
      worksheet.addRow([`Period: ${monthFrom} to ${monthTo} ${year}`]);
      worksheet.addRow([`Date Printed: ${FormatDateToWord(new Date())}`]);
      worksheet.addRow([]);

      // 3. Define Table Header (Account, Totals, then 12 pairs of Month/%)
      const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
      const headerLabels = ['Account Name', 'Total Amount', 'Total %'];
      months.forEach(m => {
        headerLabels.push(m);
        headerLabels.push(`%`);
      });

      const headerRow = worksheet.addRow(headerLabels);
      headerRow.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: 'FFFFFF' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '444444' } };
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
      });

      // 4. Helper Function to add sections
      const addSection = (sectionData, title) => {
        if (!sectionData || !sectionData.accounts) return null;

        // Section Title
        const titleRow = worksheet.addRow([title.toUpperCase()]);
        titleRow.font = { bold: true };
        titleRow.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'F2F2F2' } };

        let sectionTotals = {
          total: 0,
          totalPct: 0,
          monthlyAmts: Array(12).fill(0),
          monthlyPcts: Array(12).fill(0)
        };

        sectionData.accounts.forEach(acc => {
          const rowData = [
            acc.accountName,
            parseFloat(acc.total || 0),
            parseFloat(acc.totalPercentage || 0) / 100
          ];

          // Add Month Amount + Month % pair
          for (let i = 0; i < 12; i++) {
            const mAmt = parseFloat(acc.monthly[i]?.amount || 0);
            const mPct = parseFloat(acc.monthly[i]?.percent || 0) / 100;
            rowData.push(mAmt);
            rowData.push(mPct);

            sectionTotals.monthlyAmts[i] += mAmt;
            sectionTotals.monthlyPcts[i] += (acc.monthly[i]?.percent || 0); // Sum percentages for total row
          }

          const row = worksheet.addRow(rowData);
          
          // Formatting
          row.getCell(2).numFmt = '#,##0.00';
          row.getCell(3).numFmt = '0.00%';
          for (let i = 4; i <= 27; i++) {
            row.getCell(i).numFmt = (i % 2 === 0) ? '#,##0.00' : '0.00%';
          }

          sectionTotals.total += parseFloat(acc.total || 0);
          sectionTotals.totalPct += parseFloat(acc.totalPercentage || 0);
        });

        // Section Total Row
        const footerData = [
          `TOTAL ${title.toUpperCase()}`,
          sectionTotals.total,
          sectionTotals.totalPct / 100
        ];
        for (let i = 0; i < 12; i++) {
          footerData.push(sectionTotals.monthlyAmts[i]);
          footerData.push(sectionTotals.monthlyPcts[i] / 100);
        }

        const footerRow = worksheet.addRow(footerData);
        footerRow.font = { bold: true };
        footerRow.eachCell((cell, colIndex) => {
          if (colIndex >= 2) {
            cell.numFmt = (colIndex % 2 !== 0 && colIndex !== 2) || colIndex === 3 ? '0.00%' : '#,##0.00';
            cell.border = { top: { style: 'thin' }, bottom: { style: 'double' } };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'E9ECEF' } };
          }
        });

        worksheet.addRow([]);
        return sectionTotals;
      };

      // 5. Generate Sections
      addSection(_data.revenue, _data.revenue?.title || "Revenue");
      addSection(_data.costOfSales, _data.costOfSales?.title || "Cost of Sales");
      addSection(_data.expenses, _data.expenses?.title || "Operating Expenses");

      // 6. Column Widths
      worksheet.columns[0].width = 35; // Account Name
      worksheet.columns[1].width = 15; // Total Amt
      worksheet.columns[2].width = 10; // Total %
      for (let i = 3; i < 27; i++) {
        worksheet.columns[i].width = (i % 2 === 0) ? 12 : 9; // Amount wider than %
      }

      // 7. Save
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const filename = `Detailed_IS_with_Percentages_${year}.xlsx`;
      
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      link.click();

    } catch (error) {
      console.error("Excel Error:", error);
      setFailMessage("Error generating Excel file");
    }
    setLoading(false);
  };

  const exportISSummarizedExcel = async () => {
    if (!monthFrom || !monthTo || !year || !company) {
      setFailMessage("Please fill up all fields");
      return;
    }

    setLoading(true);

    try {
      const allData = await fetchReportData();
      const value = allData.incomeStatement?.summary;

      if (!value) {
        setFailMessage("No record found");
        setLoading(false);
        return;
      }

      // 1. Create Workbook and Worksheet
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Summarized Income Statement');

      // 2. Add Company Header Information
      worksheet.addRow([company.name?.toUpperCase()]).font = { bold: true, size: 14 };
      worksheet.addRow([company.address]);
      worksheet.addRow([`TIN: ${company.tin}`]);
      worksheet.addRow([]); // Gap

      // 3. Add Report Title and Filters
      worksheet.addRow(['STATEMENT OF COMPREHENSIVE INCOME']).font = { bold: true, size: 12 };
      worksheet.addRow([`For the Year: ${year}`]);
      worksheet.addRow([`Date Printed: ${FormatDateToWord(new Date())}`]);
      worksheet.addRow([]); // Gap

      // 4. Table Header
      const headerRow = worksheet.addRow(['Account', `Amount (${year})`]);
      headerRow.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: 'FFFFFF' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '444444' } };
        cell.alignment = { horizontal: 'center' };
      });

      // 5. Logic & Data Rows (Mirroring your PDF Logic)
      const data = [];
      
      // REVENUE & DIRECT COST
      data.push({ label: 'REVENUE', amount: value.revenue, isBold: true });
      data.push({ label: 'DIRECT COST', amount: value.directCost, isBold: true });
      
      // GROSS PROFIT
      const grossProfit = value.revenue + value.directCost;
      data.push({ label: 'GROSS PROFIT', amount: grossProfit, isTotal: true });

      // EXPENSES & OTHER INCOME
      data.push({ label: 'ADMINISTRATIVE EXPENSES', amount: value.administrativeExpenses, isBold: true });
      data.push({ label: 'OTHER INCOME', amount: value.otherIncome, isBold: true });

      // NET OPERATING INCOME
      const netOperatingIncome = grossProfit + value.administrativeExpenses + value.otherIncome;
      data.push({ label: 'NET OPERATING INCOME', amount: netOperatingIncome, isTotal: true });

      // TAX
      data.push({ label: 'PROVISION FOR INCOME TAX', amount: value.provisionForIncomeTax, isBold: true });

      // TOTAL COMPREHENSIVE INCOME
      const totalComprehensiveIncome = netOperatingIncome + value.provisionForIncomeTax;
      data.push({ label: 'TOTAL COMPREHENSIVE INCOME', amount: totalComprehensiveIncome, isGrandTotal: true });

      // 6. Apply rows to worksheet with styling
      data.forEach(item => {
        const row = worksheet.addRow([item.label, item.amount]);
        
        // Formatting the Amount cell
        const amtCell = row.getCell(2);
        amtCell.numFmt = '#,##0.00';

        if (item.isBold) {
          row.getCell(1).font = { bold: true };
        }

        if (item.isTotal) {
          row.font = { bold: true };
          amtCell.border = { top: { style: 'thin' }, bottom: { style: 'thin' } };
          row.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'F2F2F2' } };
        }

        if (item.isGrandTotal) {
          row.font = { bold: true, size: 11 };
          amtCell.border = { top: { style: 'thin' }, bottom: { style: 'double' } };
          row.eachCell(cell => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEB9C' } }; // Gold highlight
          });
        }
      });

      // 7. Adjust Column Widths
      worksheet.columns = [
        { width: 45 }, // Description
        { width: 25 }, // Amount
      ];

      // 8. Generate and Download
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const filename = `Summarized_IS_${year}.xlsx`;
      
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      link.click();
      URL.revokeObjectURL(link.href);

    } catch (error) {
      console.error("Excel Error:", error);
      setFailMessage("Error generating Excel file");
    }

    setLoading(false);
  };

  const exportISSummarizedPercentageExcel = async () => {
    if (!monthFrom || !monthTo || !year || !company) {
      setFailMessage("Please fill up all fields");
      return;
    }

    setLoading(true);

    try {
      const allData = await fetchReportData();
      const _data = allData.incomeStatement?.summaryPercentage;

      if (!_data) {
        setFailMessage("No record found");
        setLoading(false);
        return;
      }

      // 1. Create Workbook and Worksheet
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Summarized IS Percentage');

      // 2. Add Company Header Information
      worksheet.addRow([company.name?.toUpperCase()]).font = { bold: true, size: 14 };
      worksheet.addRow([company.address]);
      worksheet.addRow([`TIN: ${company.tin}`]);
      worksheet.addRow([]); // Gap

      // 3. Add Report Title and Filters
      worksheet.addRow(['SUMMARIZED INCOME STATEMENT (PERCENTAGE)']).font = { bold: true, size: 12 };
      worksheet.addRow([`Period: ${monthFrom} to ${monthTo} ${year}`]);
      worksheet.addRow([`Date Printed: ${FormatDateToWord(new Date())}`]);
      worksheet.addRow([]); // Gap

      // 4. Table Header
      const headerRow = worksheet.addRow(['Account Description', 'Amount', 'Percentage']);
      headerRow.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: 'FFFFFF' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '444444' } };
        cell.alignment = { horizontal: 'center' };
      });

      // 5. Helper Function to add sections (Revenue, COGS, Expenses)
      const addPercentageSection = (sectionData) => {
        if (!sectionData || !sectionData.accounts) return;

        // Section Title
        const titleRow = worksheet.addRow([sectionData.title?.toUpperCase()]);
        titleRow.font = { bold: true };
        titleRow.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'F2F2F2' } };

        let sectionAmount = 0;
        let sectionPercentage = 0;

        sectionData.accounts.forEach(record => {
          const row = worksheet.addRow([
            record.accountName ?? "N/A",
            parseFloat(record.amount || 0),
            parseFloat(record.percentage || 0) / 100 // Convert to decimal for Excel % format
          ]);

          // Formats
          row.getCell(2).numFmt = '#,##0.00';
          row.getCell(3).numFmt = '0.00%';

          sectionAmount += record.amount;
          sectionPercentage += record.percentage;
        });

        // Section Footer
        const footerRow = worksheet.addRow([
          `Total ${sectionData.title}`,
          sectionAmount,
          sectionPercentage / 100
        ]);

        footerRow.font = { bold: true };
        footerRow.getCell(2).numFmt = '#,##0.00';
        footerRow.getCell(3).numFmt = '0.00%';
        
        // Styling the subtotal row
        footerRow.eachCell((cell) => {
          cell.border = { top: { style: 'thin' }, bottom: { style: 'thin' } };
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'E9ECEF' } };
        });

        worksheet.addRow([]); // Small gap after section
      };

      // 6. Build Sections (Matching your PDF structure)
      addPercentageSection(_data.revenue);
      addPercentageSection(_data.costOfSales);
      addPercentageSection(_data.expenses);

      // 7. Adjust Column Widths
      worksheet.columns = [
        { width: 45 }, // Description
        { width: 20 }, // Amount
        { width: 15 }, // Percentage
      ];

      // 8. Generate and Download
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const filename = `Summarized_IS_Percentage_${year}.xlsx`;
      
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      link.click();
      URL.revokeObjectURL(link.href);

    } catch (error) {
      console.error("Excel Error:", error);
      setFailMessage("Error generating Excel file");
    }

    setLoading(false);
  };

  const exportCFSummarizedExcel = async () => {
    if (!monthFrom || !monthTo || !year || !company) {
      setFailMessage("Please fill up all fields");
      return;
    }

    setLoading(true);

    try {
      const allData = await fetchReportData();
      const _data = allData.cashFlow;

      if (!_data) {
        setFailMessage("No record found");
        setLoading(false);
        return;
      }

      // 1. Create Workbook and Worksheet
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Statement of Cash Flows');

      // 2. Add Company Header Information
      worksheet.addRow([company.name?.toUpperCase()]).font = { bold: true, size: 14 };
      worksheet.addRow([company.address]);
      worksheet.addRow([`TIN: ${company.tin}`]);
      worksheet.addRow([]); // Gap

      // 3. Add Report Title and Filters
      worksheet.addRow(['STATEMENT OF CASH FLOWS']).font = { bold: true, size: 12 };
      worksheet.addRow([`Period Covered: ${monthFrom} to ${monthTo} ${year}`]);
      worksheet.addRow([`Date Printed: ${FormatDateToWord(new Date())}`]);
      worksheet.addRow([]); // Gap

      // 4. Define Table Header
      const headerRow = worksheet.addRow(['Description', 'Amount']);
      headerRow.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: 'FFFFFF' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '444444' } };
        cell.alignment = { horizontal: 'center' };
      });

      // Helper to add a row with formatting
      const addCFRow = (label, value, isBold = false, isTotal = false) => {
        const row = worksheet.addRow([label, value === null ? "" : parseFloat(value || 0)]);
        row.getCell(2).numFmt = '#,##0.00;[Red](#,##0.00)'; // Accounting format (red for negatives)
        if (isBold) row.getCell(1).font = { bold: true };
        if (isTotal) {
          row.font = { bold: true };
          row.getCell(2).border = { top: { style: 'thin' }, bottom: { style: 'thin' } };
        }
        return row;
      };

      // 5. CASH FLOWS FROM OPERATING ACTIVITIES
      worksheet.addRow(['CASH FLOWS FROM OPERATING ACTIVITIES']).font = { bold: true };
      addCFRow('Net Income (per change in Cumulative Earnings)', _data.operatingActivities?.netIncome);
      addCFRow('Adjustments to reconcile net income to net cash:', null, true);
      addCFRow('   Depreciation', _data.operatingActivities?.depreciation);
      addCFRow('Changes in working capital:', null, true);
      addCFRow('   (Increase) Decrease in Inventory', _data.operatingActivities?.changeInventory);
      addCFRow('   (Increase) Decrease in Employee Advances', _data.operatingActivities?.changeEmployeeAdvances);
      addCFRow('   (Increase) Decrease in Other Receivables', _data.operatingActivities?.changeReceivables);
      addCFRow('   (Increase) Decrease in Other Current Assets', _data.operatingActivities?.changeOtherAssets);
      addCFRow('   Increase (Decrease) in Current Liabilities', _data.operatingActivities?.changeCurrentLiabilities);
      addCFRow('Net Cash Provided by (Used in) Operating Activities', _data.operatingActivities?.totalOperating, true, true);
      worksheet.addRow([]); // Spacer

      // 6. CASH FLOWS FROM INVESTING ACTIVITIES
      worksheet.addRow(['CASH FLOWS FROM INVESTING ACTIVITIES']).font = { bold: true };
      addCFRow('   Purchase of Delivery Equipment', _data.investingActivities?.purchaseEquipment);
      addCFRow('   Purchase of Building', _data.investingActivities?.purchaseBuilding);
      addCFRow('   Purchase of Office Renovation / Major Repairs', _data.investingActivities?.purchaseRenovations);
      addCFRow('Net Cash Provided by (Used in) Investing Activities', _data.investingActivities?.totalInvesting, true, true);
      worksheet.addRow([]); // Spacer

      // 7. CASH FLOW FROM FINANCING ACTIVITIES
      worksheet.addRow(['CASH FLOW FROM FINANCING ACTIVITIES']).font = { bold: true };
      addCFRow('   Proceeds from (Repayment of) Loan Payable', _data.financingActivities?.proceedsLoan);
      addCFRow('   Proceeds from (Repayment of) Payable to Shareholders', _data.financingActivities?.proceedsShareholders);
      addCFRow('   Issuance of Share Capital', _data.financingActivities?.issuanceShareCapital);
      addCFRow('Net Cash Provided by (Used in) Financing Activities', _data.financingActivities?.totalFinancing, true, true);
      worksheet.addRow([]); // Spacer

      // 8. CASH RECONCILIATION
      const netRow = addCFRow('NET INCREASE (DECREASE) IN CASH', _data.netIncreaseDecrease, true);
      netRow.getCell(2).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'F2F2F2' } };

      addCFRow(`Cash at Beginning of Period (${monthFrom} 01, ${year})`, _data.cashBeginning);
      
      const endRow = addCFRow(`Cash at End of Period (${monthTo} 31, ${year})`, _data.cashEnding, true);
      endRow.getCell(2).border = { top: { style: 'thin' }, bottom: { style: 'double' } };

      worksheet.addRow([]);
      const footerCheck = addCFRow('Cash at End of Period per Balance Sheet', _data.cashEnding, true);
      footerCheck.getCell(2).font = { italic: true, bold: true };

      // 9. Adjust Column Widths
      worksheet.columns = [
        { width: 55 }, // Description
        { width: 20 }, // Amount
      ];

      // 10. Generate and Download
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const filename = `Cash_Flow_Statement_${year}.xlsx`;
      
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      link.click();
      URL.revokeObjectURL(link.href);

    } catch (error) {
      console.error("Excel Error:", error);
      setFailMessage("Error generating Excel file");
    }

    setLoading(false);
  };
//#endregion






//#region Access Template
  const accountData = JSON.parse(localStorage.getItem("accountData"));
  
  if (!accountData?.accessTemplate?.hasFinancialStatement) {
    return <NoAccess />;
  }
//#endregion






//#region UI
  return (
    <>
      <div className="p-6 bg-gray-50 min-h-screen">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
          <div>
            <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
              <HistoryEduIcon fontSize="large" className="text-theme-color" />
              {`Financial Statement`}
            </h2>
            <p className="text-gray-500 mt-1">Financial performance and financial position.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="max-w-2xl">
            <label className="block text-sm font-medium mb-1 text-gray-700 ">
              Company <span className="text-red-500">*</span>
            </label>
            <CompanySelect data={company} setData={setCompany} isEditable={true}/>
          </div>

          <div className="max-w-2xl">
            <label className="block text-sm font-medium mb-1 text-gray-700 ">
              Year
            </label>
            <YearSelect data={year} setData={setYear} isEditable={true}/>
          </div>

          <div className="max-w-2xl">
            <label className="block text-sm font-medium mb-1 text-gray-700 ">
              Month From
            </label>
            <MonthSelect data={monthFrom} setData={setMonthFrom} isEditable={true}/>
          </div>

          <div className="max-w-2xl">
            <label className="block text-sm font-medium mb-1 text-gray-700 ">
              Month To
            </label>
            <MonthSelect data={monthTo} setData={setMonthTo} isEditable={true}/>
          </div>

        </div>

        <div className="mt-5 p-6 bg-white rounded-2xl shadow-sm border border-gray-100">
          <h2 className="text-lg font-bold text-gray-800 mb-6 px-2">Generate Reports</h2>

          {/* Header Row - Hidden on Mobile */}
          <div className="hidden md:grid grid-cols-12 gap-4 px-4 mb-2 text-xs font-black uppercase tracking-widest text-gray-400">
            <div className="col-span-2">Report Category</div>
            <div className="col-span-5">PDF Reports</div>
            <div className="col-span-5 border-l border-gray-100">Excel Reports</div>
          </div>

          <div className="space-y-3">
            {[
              { 
                label: "Balance Sheet", 
                pdf_detailed: generateBSDetailed, 
                pdf_summarized: generateBSSummarized, 
                excel_detailed: exportBSDetailedExcel, // Replace with your Excel function
                excel_summarized: exportBSSummarizedExcel, // Replace with your Excel function
              },
              { 
                label: "Income Statement", 
                pdf_detailed: generateISDetailed, 
                pdf_summarized: generateISSummarized, 
                pdf_extra: generateISSummarizedPercentage, 
                excel_detailed: exportISDetailedExcel, // Replace with your Excel function
                excel_summarized: exportISSummarizedExcel, // Replace with your Excel function
                excel_extra: exportISSummarizedPercentageExcel, // Replace with your Excel function
              },
              { 
                label: "Cash Flow", 
                pdf_summarized: generateCFSummarized, 
                excel_summarized: exportCFSummarizedExcel, // Replace with your Excel function
              },
            ].map((group, idx) => (
              <div key={idx} className="grid grid-cols-1 md:grid-cols-12 items-center gap-4 p-4 rounded-xl border border-gray-100 hover:bg-gray-50 transition-colors">
                
                {/* Label */}
                <div className="col-span-2">
                  <span className="text-sm font-bold text-gray-700">{group.label}</span>
                </div>

                {/* PDF Column */}
                <div className="col-span-5 flex flex-wrap gap-2">
                  {group.pdf_detailed &&
                    <button 
                      onClick={group.pdf_detailed} 
                      className="flex items-center gap-2 bg-red-50 text-red-700 border border-red-100 px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-red-100 transition-all active:scale-95"
                      title="Download PDF Detailed"
                    >
                      <RestartAltIcon sx={{ fontSize: 16 }} /> Detailed
                    </button>
                  }
                  
                  {group.pdf_summarized &&
                    <button 
                      onClick={group.pdf_summarized} 
                      className="flex items-center gap-2 bg-white text-gray-700 border border-gray-300 px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-gray-50 transition-all active:scale-95"
                    >
                      Summarized
                    </button>
                  }

                  {group.pdf_extra && (
                    <button 
                      onClick={group.pdf_extra} 
                      className="flex items-center gap-2 bg-blue-50 text-blue-700 border border-blue-100 px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-blue-100 transition-all active:scale-95"
                    >
                      Summarized w/ %
                    </button>
                  )}
                </div>

                {/* Excel Column */}
                <div className="col-span-5 flex flex-wrap gap-2 border-t md:border-t-0 md:border-l border-gray-100 pt-3 md:pt-0">
                  {group.excel_detailed &&
                    <button 
                      onClick={group.excel_detailed} 
                      className="flex items-center gap-2 bg-green-600 text-white px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-green-700 shadow-sm transition-all active:scale-95"
                    >
                      Excel Detailed
                    </button>
                  }
                  
                  {group.excel_summarized &&
                    <button 
                      onClick={group.excel_summarized} 
                      className="flex items-center gap-2 bg-green-50 text-green-700 border border-green-200 px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-green-100 transition-all active:scale-95"
                    >
                      Excel Summ.
                    </button>
                  }
                  
                  
                  { group.excel_extra &&
                    <button 
                      onClick={group.excel_extra} 
                      className="flex items-center gap-2 bg-green-50 text-green-700 border border-green-200 px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-green-100 transition-all active:scale-95"
                    >
                      Excel Summ. w/ %
                    </button>
                  }
                </div>

              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="fixed top-5 right-5 z-50 flex flex-col space-y-2">
        <SuccessMessage message={successMessage} onClose={() => setSuccessMessage("")} />
        <FailMessage message={failMessage} onClose={() => setFailMessage("")} />
      </div>

      { loading && <Loading />}
    </>
  );
//#endregion
}